import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { withError } from '@/react-tools/react-query';

import { Registry } from '../types/registry';

import { buildUrl } from './build-url';
import { queryKeys } from './query-keys';

type Params = {
  endpointId: number;
  mediaType?: string;
};

/**
 * useRegistryRepositories - Get repositories from an OCI registry
 */
export function useRegistryRepositories<T = string[]>(
  registryId: Registry['Id'],
  params: Params,
  queryOptions: UseQueryOptions<string[], unknown, T> = {},
  showError = true
) {
  return useQuery({
    queryKey: queryKeys.repositoriesList(registryId, params.mediaType),
    queryFn: () => getRegistryRepositories(registryId, params),
    // registryId starts at 1
    enabled: !!registryId,
    ...(showError && withError('Unable to retrieve registry repositories')),
    retry: false,
    ...queryOptions,
  });
}

async function getRegistryRepositories(
  registryId: Registry['Id'],
  params: Params
) {
  try {
    const { data } = await axios.get<string[]>(
      buildUrl(registryId, 'repositories'),
      {
        params,
      }
    );
    return data;
  } catch (err) {
    throw parseAxiosError(err, 'Unable to retrieve registry repositories');
  }
}
